// Central brand vocabulary so no component hardcodes "Tesla" for a Ford
// (or future OEM) session. Everything derives from vehicle.brand.

const API = process.env.REACT_APP_BACKEND_URL || "";

// ---------------------------------------------------------------------------
// EV_BATTERY_WARRANTY — warranty terms come from EVIQ's own rules database
// (admin-curated, /api/warranty-db/public), NOT hardcoded brand constants.
// Loaded once per session; hardcoded baselines remain only as a fallback
// until the fetch resolves (or if the API is unreachable).
let WARRANTY_RULES = null;
let rulesFetch = null;

export function initWarrantyRules() {
  if (WARRANTY_RULES || rulesFetch || !API) return rulesFetch;
  rulesFetch = fetch(`${API}/api/warranty-db/public`)
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => { WARRANTY_RULES = j?.rules?.length ? j.rules : null; })
    .catch(() => { rulesFetch = null; });
  return rulesFetch;
}

const MODEL_FAMILIES = [
  "mustang mach-e", "f-150 lightning", "e-transit",
  "model 3", "model y", "model s", "model x", "cybertruck", "roadster",
  "ex40", "xc40", "c40", "ec40", "ex30", "ex90", "polestar",
];

/** Resolve {years, miles, threshold} from the EVIQ warranty DB, or null
 *  when rules aren't loaded / no confident match (caller falls back). */
export function dbWarrantyFor({ brand, model, year, trim, preferMiles } = {}) {
  if (!WARRANTY_RULES || !model) return null;
  const b = String(brand || "").toLowerCase();
  const m = String(model).toLowerCase();
  const family = MODEL_FAMILIES.find((f) => m.includes(f));
  if (!family) return null;
  const y = Number(year) || new Date().getFullYear();
  const candidates = WARRANTY_RULES.filter((r) =>
    r.manufacturer.toLowerCase() === b &&
    r.model.toLowerCase().includes(family) &&
    y >= r.model_year_start && y <= r.model_year_end);
  if (!candidates.length) return null;
  let rule = candidates[0];
  if (candidates.length > 1) {
    if (typeof preferMiles === "number") {
      rule = candidates.find((r) => r.warranty_miles === preferMiles) || rule;
    } else {
      const upper = /long range|performance|plaid|extended|\blr\b/.test(
        `${String(trim || "").toLowerCase()} ${m}`);
      rule = candidates.find((r) => /long range|performance/i.test(r.model) === upper) || rule;
    }
  }
  if (rule.warranty_miles == null || rule.capacity_threshold_percent == null) return null;
  return {
    years: rule.warranty_years,
    miles: rule.warranty_miles,
    threshold: rule.capacity_threshold_percent,
  };
}

export function isFord(vehicle) {
  return vehicle?.brand === "ford";
}

export function isVolvo(vehicle) {
  return vehicle?.brand === "volvo";
}

/** "Tesla" | "Ford" | "Volvo" — the OEM display name. */
export function brandLabel(vehicle) {
  if (isFord(vehicle)) return "Ford";
  if (isVolvo(vehicle)) return "Volvo";
  return "Tesla";
}

/** OEM service channel, e.g. "Ford Service" / "Tesla Service". */
export function serviceLabel(vehicle) {
  return `${brandLabel(vehicle)} Service`;
}

/** OEM companion app name. */
export function appLabel(vehicle) {
  if (isFord(vehicle)) return "FordPass app";
  if (isVolvo(vehicle)) return "Volvo Cars app";
  return "Tesla app";
}

/** Fast-charging verb/network term for the brand. */
export function fastChargeLabel(vehicle) {
  return isFord(vehicle) || isVolvo(vehicle) ? "DC fast-charging" : "Supercharging";
}

export function fastChargeVerb(vehicle) {
  return isFord(vehicle) || isVolvo(vehicle) ? "DC fast-charge" : "Supercharge";
}

/**
 * Brand-correct HV battery warranty resolver. Order of truth:
 *   1. vehicle.warranty (live, trim-exact — itself DB-backed in context)
 *   2. EVIQ's EV_BATTERY_WARRANTY database rule for this model/year
 *   3. OEM baseline constants (only until the DB rules load)
 */
export function warrantyFor(vehicle) {
  const w = vehicle?.warranty || {};
  const dbW = dbWarrantyFor({
    brand: brandLabel(vehicle),
    model: vehicle?.model || vehicle?.name || "",
    year: vehicle?.year,
    trim: vehicle?.trim || vehicle?.short || vehicle?.name || "",
    preferMiles: typeof w.miles === "number" ? w.miles : undefined,
  });
  const base = dbW || (isFord(vehicle) || isVolvo(vehicle)
    ? { years: 8, miles: 100000, threshold: 70 }
    : { years: 8, miles: 120000, threshold: 70 });
  return {
    years: typeof w.years === "number" ? w.years : base.years,
    miles: typeof w.miles === "number" ? w.miles : base.miles,
    threshold: typeof w.threshold === "number" ? w.threshold : base.threshold,
  };
}
